import {
    ButtonHTMLAttributes,
    ChangeEvent,
    Fragment,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useEffect,
    useState,
} from "react";
import {Button} from "@/components/buttons/Button";
import {FaMapMarkerAlt} from "react-icons/fa";
import classNames from "classnames";

interface Props<D = string> {
    placeholder?: string;
    resetOnSelect?: boolean;
    emptyText?: string;
    minLength?: number;
    onSelect: (d: D) => void;
    onInputChange: (q: string) => void;
    results: Array<{
        render: ReactNode;
        data: D;
        address?: string;
    }>;
    showButton?: boolean;
    showMapMarkerIcon?: boolean;
}

function AutocompleteInput<D>({
    placeholder,
    resetOnSelect,
    onSelect,
    onInputChange,
    results,
    showButton,
    showMapMarkerIcon,
    minLength = 1,
    emptyText = "No results found",
}: PropsWithChildren<Props<D>>): ReactElement {

    const [
        inputValue,
        setInputValue,
    ] = useState<string>("");
    const [
        dismissResults,
        setDismissResults,
    ] = useState<boolean>(false);
    const [
        resultData,
        setResultData,
    ] = useState<D>();

    useEffect(() => {
        if (inputValue.length >= minLength) {
            onInputChange(inputValue);
        }
    }, [inputValue]);

    function onClickHelper(data: D): void {
        onSelect(data);

        if (resetOnSelect) {
            setInputValue("");
        }
    }

    const renderResults: JSX.Element =
        <ul className="dp-autocomplete-input_results_list">
            {results.map((result, index) => <li
                key={`autocomplete-result-${index}`}
                className="dp-autocomplete-input_results_list_item"
            >
                <button
                    type="button"
                    onClick={() => {
                        if (showButton) {
                            setDismissResults(true);
                            setInputValue(result.address);
                            setResultData(result.data);
                        } else {
                            onClickHelper(result.data);
                        }
                    }}
                >
                    {result.render}
                </button>
            </li>)}
        </ul>;

    /**
     * No results from autocomplete api, but check if the input component actually has text before showing the empty response.
     */
    const renderNoResults: JSX.Element = emptyText
        ? <div className="dp-autocomplete-input_results_empty">
            {emptyText}
        </div>
        : <Fragment/>;

    function onChangeHelper(e: ChangeEvent<HTMLInputElement>) {
        setDismissResults(false);
        setResultData(undefined);
        setInputValue(e.target.value);
    }

    return (
        <>
            <div className={classNames("dp-autocomplete-input", {
                "show-icon": showMapMarkerIcon,
            })}>
                {showMapMarkerIcon && <FaMapMarkerAlt className="dp-autocomplete-input_icon"/> }
                <div className="dp-autocomplete-input_value">
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={onChangeHelper}
                    />
                </div>
                {inputValue.length >= minLength && !dismissResults &&
                <div className="dp-autocomplete-input_results">
                    {results.length > 0
                        ? renderResults
                        : renderNoResults}
                </div>
                }
            </div>
            {showButton &&
            <Button variant="primary"
                onClick={() => resultData && onClickHelper(resultData)}
                {...{disabled: !resultData}}
                className="dp-autocomplete-input_button"
            >
                Find food near me
            </Button>}
        </>
    );

}

export default AutocompleteInput;
